// Organization settings hook - persists to Supabase organizations.settings
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { AIProvider } from '@/types/database'
import type { Json } from '@/types/database.generated'

export interface OrgSettings {
  // Supported D365 F&O systems (configurable per org, consumed by issue creation + agents)
  supportedVersions: string[]
  supportedProducts: string[]
  supportedDeploymentTypes: string[]
  // AI configuration
  aiProvider: AIProvider
  defaultChatModel: string
  defaultEmbeddingModel: string
  defaultTemperature: number
  searchConfidenceThreshold: number
  maxSearchResults: number
  enableAiLogging: boolean
  enableRateLimiting: boolean
  defaultRateLimit: number
}

const DEFAULT_SETTINGS: OrgSettings = {
  supportedVersions: ['10.0.37', '10.0.38', '10.0.39', '10.0.40', '10.0.41', '10.0.42', '10.0.43', '10.0.44', '10.0.45', '10.0.46'],
  supportedProducts: ['finance', 'supply_chain', 'commerce', 'human_resources', 'project_operations'],
  supportedDeploymentTypes: ['cloud', 'on_premises', 'cloud_hosted_dev'],
  aiProvider: 'kaycoo',
  defaultChatModel: 'gpt-4o-mini',
  defaultEmbeddingModel: 'text-embedding-3-small',
  defaultTemperature: 0.3,
  searchConfidenceThreshold: 0.70,
  maxSearchResults: 10,
  enableAiLogging: true,
  enableRateLimiting: true,
  defaultRateLimit: 60,
}

export function useSettings() {
  const { user, organization } = useAuth()
  const [settings, setSettings] = useState<OrgSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!organization) return
    // Merge DB settings over defaults
    const dbSettings = (organization as unknown as { settings?: Record<string, unknown> })?.settings
    if (dbSettings && typeof dbSettings === 'object') {
      setSettings((prev) => ({ ...prev, ...dbSettings } as OrgSettings))
    }
    setLoading(false)
  }, [organization])

  const saveSettings = useCallback(async (updated: Partial<OrgSettings>) => {
    if (!user?.organizationId) return
    setSaving(true)
    setError(null)
    try {
      const merged = { ...settings, ...updated }
      const { error: dbError } = await supabase
        .from('organizations')
        .update({ settings: merged as unknown as Json })
        .eq('id', user.organizationId)

      if (dbError) throw dbError
      setSettings(merged)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save settings'
      setError(msg)
      throw err
    } finally {
      setSaving(false)
    }
  }, [user?.organizationId, settings])

  return { settings, loading, saving, error, saveSettings, DEFAULT_SETTINGS }
}
